import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Star } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { inr } from "@/lib/format";
import { TID } from "@/lib/testIds";

const inputCls =
  "w-full border border-forest/10 bg-panel px-3 py-2 text-sm text-white placeholder-white/30 transition-colors focus:border-forest/60 focus:outline-none";
const labelCls = "block font-mono text-[10px] uppercase tracking-[0.25em] text-white/50";

function Field({ label, tid, ...props }) {
  return (
    <label className="block space-y-1.5">
      <span className={labelCls}>{label}</span>
      <input className={inputCls} data-testid={tid} {...props} />
    </label>
  );
}

function Area({ label, tid, rows = 3, ...props }) {
  return (
    <label className="block space-y-1.5">
      <span className={labelCls}>{label}</span>
      <textarea className={inputCls} data-testid={tid} rows={rows} {...props} />
    </label>
  );
}

function Login({ onUser }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onUser(data);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm space-y-5 border border-forest/10 bg-inkcard/60 p-8">
        <Logo variant="light" />
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Content control — owner access</p>
        <Field label="Email" tid={TID.admin.loginEmail} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@offcraft.studio" />
        <Field label="Password" tid={TID.admin.loginPassword} type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
        {error && (
          <p data-testid={TID.admin.loginError} className="border border-red-400/30 bg-red-400/10 px-3 py-2 text-xs text-red-300">
            {error}
          </p>
        )}
        <button data-testid={TID.admin.loginSubmit} disabled={busy} className="w-full bg-crimson px-6 py-3 font-display text-sm font-bold text-white transition-colors hover:bg-crimsonlight disabled:opacity-50">
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <Link to="/" className="block text-center font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-white">
          ← Back to site
        </Link>
      </form>
    </div>
  );
}

const TABS = ["packages", "maintenance", "offers", "notes", "testimonials", "enquiries"];

export default function Admin() {
  const [user, setUser] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState("packages");
  const [enquiries, setEnquiries] = useState(null);
  const [testimonials, setTestimonials] = useState(null);
  const [tForm, setTForm] = useState({ name: "", role: "", quote: "", rating: 5, photo: "" });
  const [replyDraft, setReplyDraft] = useState({});
  const [busyT, setBusyT] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) api.get("/config").then(({ data }) => setCfg(data)).catch(() => toast.error("Could not load site configuration"));
  }, [user]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(false);
  };

  const openTab = (t) => {
    setTab(t);
    if (t === "enquiries" && !enquiries) loadEnquiries();
    if (t === "testimonials" && !testimonials) loadTestimonials();
  };

  const loadTestimonials = () => {
    api.get("/admin/testimonials").then(({ data }) => setTestimonials(data)).catch(() => toast.error("Could not load testimonials"));
  };

  const addTestimonial = async () => {
    if (!tForm.name.trim() || !tForm.quote.trim()) {
      toast.error("Client name and their words are required.");
      return;
    }
    setBusyT(true);
    try {
      await api.post("/admin/testimonials", tForm);
      setTForm({ name: "", role: "", quote: "", rating: 5, photo: "" });
      loadTestimonials();
      toast.success("Testimonial posted — live on the home page");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusyT(false);
    }
  };

  const toggleTestimonial = async (t) => {
    try {
      await api.patch(`/admin/testimonials/${t._id}`, { published: !t.published });
      loadTestimonials();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteTestimonial = async (id) => {
    try {
      await api.delete(`/admin/testimonials/${id}`);
      loadTestimonials();
      toast.success("Testimonial removed");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const handlePhoto = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new window.Image();
      img.onload = () => {
        const size = 200;
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        const min = Math.min(img.width, img.height);
        ctx.drawImage(img, (img.width - min) / 2, (img.height - min) / 2, min, min, 0, 0, size, size);
        setTForm((f) => ({ ...f, photo: canvas.toDataURL("image/jpeg", 0.85) }));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const sendReply = async (en) => {
    const msg = (replyDraft[en._id] || "").trim();
    if (!msg) {
      toast.error("Write a reply first.");
      return;
    }
    try {
      await api.post(`/admin/enquiries/${en._id}/reply`, { message: msg });
      setReplyDraft((d) => ({ ...d, [en._id]: "" }));
      loadEnquiries();
      toast.success("Reply sent — it landed in their email inbox");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const loadEnquiries = () => {
    api.get("/admin/enquiries").then(({ data }) => setEnquiries(data)).catch(() => toast.error("Could not load enquiries"));
  };

  const updPackage = (id, patch) => {
    setCfg((c) => ({
      ...c,
      packages: c.packages.map((p) => {
        if (p.id !== id) return patch.__exclusive ? { ...p, recommended: false } : p;
        const { __exclusive, ...rest } = patch;
        return { ...p, ...rest };
      }),
    }));
  };

  const save = async () => {
    try {
      const payload = {
        ...cfg,
        packages: cfg.packages.map((p) => ({
          ...p,
          dev_fee: Number(p.dev_fee) || 0,
          maintenance: Number(p.maintenance) || 0,
          features: p.features.filter((f) => String(f).trim()),
        })),
        maintenance_plan: {
          ...cfg.maintenance_plan,
          price: Number(cfg.maintenance_plan.price) || 0,
          features: cfg.maintenance_plan.features.filter((f) => String(f).trim()),
        },
        offers: { ...cfg.offers, options: cfg.offers.options.map((o) => ({ months: Number(o.months) || 0, discount: Number(o.discount) || 0, label: o.label })) },
      };
      await api.put("/admin/config", payload);
      setCfg(payload);
      toast.success("Saved — the live site is updated");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (user === null) return <div className="flex min-h-screen items-center justify-center bg-obsidian"><span className="animate-pulse font-mono text-[11px] tracking-[0.4em] text-white/40">CHECKING SESSION…</span></div>;
  if (user === false) return <Login onUser={setUser} />;
  if (!cfg) return <div className="flex min-h-screen items-center justify-center bg-obsidian"><span className="animate-pulse font-mono text-[11px] tracking-[0.4em] text-white/40">LOADING…</span></div>;

  return (
    <div className="min-h-screen bg-obsidian pb-32">
      <header className="sticky top-0 z-30 border-b border-forest/10 bg-obsidian/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <Logo variant="light" />
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 sm:inline">Content control</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 hover:text-white">
              View site
            </Link>
            <button data-testid={TID.admin.logout} onClick={logout} className="flex items-center gap-2 border border-forest/10 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 hover:border-red-400/50 hover:text-red-300">
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
        <div className="mx-auto flex max-w-6xl gap-1 overflow-x-auto px-6 pb-3">
          {TABS.map((t) => (
            <button
              key={t}
              data-testid={TID.admin.tab(t)}
              onClick={() => openTab(t)}
              className={`shrink-0 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${tab === t ? "bg-crimson text-white" : "text-white/50 hover:text-white"}`}
            >
              {t}
            </button>
          ))}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-8">
        {tab === "packages" && (
          <div className="space-y-6">
            <p className="text-sm text-white/50">Every value here flows to every page of the website the moment you save.</p>
            {cfg.packages.map((p) => (
              <div key={p.id} className="space-y-4 border border-forest/10 p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className={labelCls}>Package {p.number} — current: {inr(p.dev_fee)} + {inr(p.maintenance)}/mo</span>
                  <label className="flex items-center gap-2 text-xs text-white/70">
                    <input
                      type="checkbox"
                      checked={!!p.recommended}
                      data-testid={`admin-pkg-recommended-${p.id}`}
                      onChange={(e) => updPackage(p.id, { recommended: e.target.checked, __exclusive: true })}
                      className="accent-crimson"
                    />
                    Highlight as recommended
                  </label>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <Field label="Display name" tid={`admin-pkg-name-${p.id}`} value={p.name} onChange={(e) => updPackage(p.id, { name: e.target.value })} />
                  <Field label="Number" tid={`admin-pkg-number-${p.id}`} value={p.number} onChange={(e) => updPackage(p.id, { number: e.target.value })} />
                  <Field label="CTA label" tid={`admin-pkg-cta-${p.id}`} value={p.cta} onChange={(e) => updPackage(p.id, { cta: e.target.value })} />
                  <Field label="Development fee (₹)" tid={`admin-pkg-fee-${p.id}`} type="number" value={p.dev_fee} onChange={(e) => updPackage(p.id, { dev_fee: e.target.value })} />
                  <Field label="Monthly maintenance (₹)" tid={`admin-pkg-maintenance-${p.id}`} type="number" value={p.maintenance} onChange={(e) => updPackage(p.id, { maintenance: e.target.value })} />
                  <Field label="Fee prefix (e.g. Starting at)" tid={`admin-pkg-prefix-${p.id}`} value={p.price_prefix} onChange={(e) => updPackage(p.id, { price_prefix: e.target.value })} placeholder='Leave empty unless "Starting at"' />
                </div>
                <Area label="Description" tid={`admin-pkg-desc-${p.id}`} value={p.description} onChange={(e) => updPackage(p.id, { description: e.target.value })} />
                <Area label="Features (one per line)" tid={`admin-pkg-features-${p.id}`} rows={6} value={p.features.join("\n")} onChange={(e) => updPackage(p.id, { features: e.target.value.split("\n") })} />
                <Area label="Ideal clients" tid={`admin-pkg-ideal-${p.id}`} rows={2} value={p.ideal_for} onChange={(e) => updPackage(p.id, { ideal_for: e.target.value })} />
                <Area label="Card footnote (optional)" tid={`admin-pkg-scopenote-${p.id}`} rows={2} value={p.scope_note} onChange={(e) => updPackage(p.id, { scope_note: e.target.value })} />
              </div>
            ))}
          </div>
        )}

        {tab === "maintenance" && cfg.maintenance_plan && (
          <div className="space-y-4 border border-forest/10 p-6">
            <label className="flex items-center gap-2 text-xs text-white/70">
              <input
                type="checkbox"
                checked={cfg.maintenance_plan.enabled !== false}
                data-testid="admin-plan-enabled"
                onChange={(e) => setCfg({ ...cfg, maintenance_plan: { ...cfg.maintenance_plan, enabled: e.target.checked } })}
                className="accent-crimson"
              />
              Show maintenance plan on the site
            </label>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Plan name" tid="admin-plan-name" value={cfg.maintenance_plan.name} onChange={(e) => setCfg({ ...cfg, maintenance_plan: { ...cfg.maintenance_plan, name: e.target.value } })} />
              <Field label="Monthly price (₹)" tid="admin-plan-price" type="number" value={cfg.maintenance_plan.price} onChange={(e) => setCfg({ ...cfg, maintenance_plan: { ...cfg.maintenance_plan, price: e.target.value } })} />
            </div>
            <Area label="Intro line" tid="admin-plan-intro" value={cfg.maintenance_plan.intro} onChange={(e) => setCfg({ ...cfg, maintenance_plan: { ...cfg.maintenance_plan, intro: e.target.value } })} />
            <Area label="Included work (one per line)" tid="admin-plan-features" rows={6} value={cfg.maintenance_plan.features.join("\n")} onChange={(e) => setCfg({ ...cfg, maintenance_plan: { ...cfg.maintenance_plan, features: e.target.value.split("\n") } })} />
            <Area label="Limits note (no unlimited changes)" tid="admin-plan-note" rows={3} value={cfg.maintenance_plan.note} onChange={(e) => setCfg({ ...cfg, maintenance_plan: { ...cfg.maintenance_plan, note: e.target.value } })} />
          </div>
        )}

        {tab === "offers" && cfg.offers && (
          <div className="space-y-4 border border-forest/10 p-6">
            <label className="flex items-center gap-2 text-xs text-white/70">
              <input
                type="checkbox"
                checked={cfg.offers.enabled}
                data-testid="admin-offers-enabled"
                onChange={(e) => setCfg({ ...cfg, offers: { ...cfg.offers, enabled: e.target.checked } })}
                className="accent-crimson"
              />
              Show commitment offer (no fake discounts — real terms only)
            </label>
            <Field label="Offer title" tid="admin-offers-title" value={cfg.offers.title} onChange={(e) => setCfg({ ...cfg, offers: { ...cfg.offers, title: e.target.value } })} />
            <Area label="Terms disclosure (shown under the toggle)" tid="admin-offers-disclosure" rows={3} value={cfg.offers.disclosure} onChange={(e) => setCfg({ ...cfg, offers: { ...cfg.offers, disclosure: e.target.value } })} />
            {cfg.offers.options.map((o, i) => (
              <div key={i} className="grid gap-4 border border-forest/10 p-4 sm:grid-cols-3">
                <Field label="Commitment (months)" tid={`admin-offer-months-${i}`} type="number" value={o.months} onChange={(e) => setCfg({ ...cfg, offers: { ...cfg.offers, options: cfg.offers.options.map((x, j) => (j === i ? { ...x, months: e.target.value } : x)) } })} />
                <Field label="One-time fee discount (₹)" tid={`admin-offer-discount-${i}`} type="number" value={o.discount} onChange={(e) => setCfg({ ...cfg, offers: { ...cfg.offers, options: cfg.offers.options.map((x, j) => (j === i ? { ...x, discount: e.target.value } : x)) } })} />
                <Field label="Label" tid={`admin-offer-label-${i}`} value={o.label} onChange={(e) => setCfg({ ...cfg, offers: { ...cfg.offers, options: cfg.offers.options.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) } })} />
              </div>
            ))}
          </div>
        )}

        {tab === "notes" && (
          <div className="space-y-4 border border-forest/10 p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Brand name" tid="admin-brand-name" value={cfg.brand_name} onChange={(e) => setCfg({ ...cfg, brand_name: e.target.value })} />
              <Field label="WhatsApp number (digits with country code)" tid="admin-whatsapp" value={cfg.whatsapp_number} onChange={(e) => setCfg({ ...cfg, whatsapp_number: e.target.value })} placeholder="919XXXXXXXXX" />
              <Field label="Contact email" tid="admin-contact-email" value={cfg.contact.email} onChange={(e) => setCfg({ ...cfg, contact: { ...cfg.contact, email: e.target.value } })} />
              <Field label="Response time line" tid="admin-contact-response" value={cfg.contact.response_time} onChange={(e) => setCfg({ ...cfg, contact: { ...cfg.contact, response_time: e.target.value } })} />
            </div>
            <Area label="Tagline" tid="admin-tagline" value={cfg.tagline} onChange={(e) => setCfg({ ...cfg, tagline: e.target.value })} />
            <Area label="Scope note (complex builds quoted separately)" tid="admin-notes-scope" rows={3} value={cfg.notes.scope} onChange={(e) => setCfg({ ...cfg, notes: { ...cfg.notes, scope: e.target.value } })} />
            <Area label="Third-party costs note (domain, hosting, plugins)" tid="admin-notes-thirdparty" rows={3} value={cfg.notes.third_party} onChange={(e) => setCfg({ ...cfg, notes: { ...cfg.notes, third_party: e.target.value } })} />
            <Area label="Maintenance scope note" tid="admin-notes-maintscope" rows={3} value={cfg.notes.maintenance_scope} onChange={(e) => setCfg({ ...cfg, notes: { ...cfg.notes, maintenance_scope: e.target.value } })} />
          </div>
        )}

        {tab === "testimonials" && (
          <div className="grid gap-8 lg:grid-cols-12">
            <div className="space-y-4 lg:col-span-5">
              <div className="space-y-4 border border-white/10 p-6">
                <p className="text-sm text-white/50">Post your clients' words — they appear on the home page the moment you save.</p>
                <Field label="Client name *" tid="admin-testimonial-name" value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} placeholder="e.g. Priya Sharma" />
                <Field label="Role / company" tid="admin-testimonial-role" value={tForm.role} onChange={(e) => setTForm({ ...tForm, role: e.target.value })} placeholder="e.g. Founder, Sharma Interiors" />
                <Area label="Their words *" tid="admin-testimonial-quote" rows={4} value={tForm.quote} onChange={(e) => setTForm({ ...tForm, quote: e.target.value })} placeholder="What did they say about working with you?" />
                <label className="block space-y-1.5">
                  <span className={labelCls}>Rating</span>
                  <select data-testid="admin-testimonial-rating" className={inputCls} value={tForm.rating} onChange={(e) => setTForm({ ...tForm, rating: Number(e.target.value) })}>
                    {[5, 4, 3, 2, 1].map((n) => (
                      <option key={n} value={n} className="bg-panel">
                        {n} star{n > 1 ? "s" : ""}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="flex items-center gap-4">
                  {tForm.photo ? (
                    <img src={tForm.photo} alt="" className="h-12 w-12 rounded-full border border-white/10 object-cover" />
                  ) : (
                    <span className="flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-panel font-display text-sm font-bold text-crimson">
                      {tForm.name.trim() ? tForm.name.trim()[0].toUpperCase() : "?"}
                    </span>
                  )}
                  <label className="cursor-pointer font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 hover:text-white">
                    {tForm.photo ? "Change photo" : "Add headshot (optional)"}
                    <input type="file" accept="image/*" data-testid="admin-testimonial-photo" onChange={handlePhoto} className="hidden" />
                  </label>
                  {tForm.photo ? (
                    <button type="button" onClick={() => setTForm({ ...tForm, photo: "" })} className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 hover:text-red-300">
                      Remove
                    </button>
                  ) : null}
                </div>
                <button
                  data-testid="admin-testimonial-submit"
                  onClick={addTestimonial}
                  disabled={busyT}
                  className="w-full bg-crimson px-6 py-3 font-display text-sm font-bold text-white transition-colors hover:bg-crimsondark disabled:opacity-50"
                >
                  {busyT ? "Posting…" : "Post testimonial"}
                </button>
              </div>
              <p className="text-xs leading-relaxed text-white/40">The three starter testimonials are sample placeholders — replace or delete them and post your real client words.</p>
            </div>
            <div className="space-y-4 lg:col-span-7" data-testid="admin-testimonials-list">
              {testimonials === null && <p className="text-sm text-white/50">Loading…</p>}
              {Array.isArray(testimonials) && testimonials.length === 0 && <p className="text-sm text-white/50">No testimonials yet.</p>}
              {Array.isArray(testimonials) &&
                testimonials.map((t) => (
                  <div key={t._id} data-testid="admin-testimonial-item" className="border border-white/10 p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-1">
                          {Array.from({ length: t.rating }).map((_, i) => (
                            <Star key={i} className="h-3.5 w-3.5 fill-crimson text-crimson" />
                          ))}
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-white/80">"{t.quote}"</p>
                        <div className="mt-3 flex items-center gap-3">
                          {t.photo ? (
                            <img src={t.photo} alt="" className="h-10 w-10 rounded-full border border-white/10 object-cover" />
                          ) : (
                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-panel font-display text-sm font-bold text-crimson">
                              {(t.name || "?").trim()[0].toUpperCase()}
                            </span>
                          )}
                          <span>
                            <span className="block font-display text-sm font-bold text-white">{t.name}</span>
                            {t.role ? <span className="block text-xs text-white/45">{t.role}</span> : null}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <label className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">
                          <input type="checkbox" checked={t.published} data-testid={`admin-testimonial-publish-${t._id}`} onChange={() => toggleTestimonial(t)} className="accent-crimson" />
                          {t.published ? "Live" : "Hidden"}
                        </label>
                        <button
                          data-testid={`admin-testimonial-delete-${t._id}`}
                          onClick={() => deleteTestimonial(t._id)}
                          className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 transition-colors hover:text-red-300"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}

        {tab === "enquiries" && (
          <div data-testid={TID.admin.enquiriesList} className="space-y-4">
            {enquiries === null && <p className="text-sm text-white/50">Loading enquiries…</p>}
            {Array.isArray(enquiries) && enquiries.length === 0 && <p className="text-sm text-white/50">No enquiries yet — they will appear here the moment a visitor sends one.</p>}
            {Array.isArray(enquiries) &&
              enquiries.map((en) => (
                <div key={en._id} data-testid={TID.admin.enquiryItem} className="border border-forest/10 p-5">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-display text-sm font-bold text-crimson">{en.package_name || "General enquiry"}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{new Date(en.created_at).toLocaleString("en-IN")}</span>
                  </div>
                  <div className="mt-3 grid gap-1 text-sm text-white/75 sm:grid-cols-3">
                    <span>{en.name}</span>
                    <span className="text-white/50">{en.email}</span>
                    <span className="text-white/50">{en.phone || "—"}</span>
                  </div>
                  {en.message ? <p className="mt-3 border-l border-forest/40 pl-3 text-sm leading-relaxed text-white/60">{en.message}</p> : null}
                  {(en.replies || []).length > 0 && (
                    <div className="mt-3 space-y-2">
                      {en.replies.map((r, i) => (
                        <div key={i} className="border-l-2 border-emerald-400/50 bg-white/5 px-3 py-2">
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-400">
                            Replied — {new Date(r.sent_at).toLocaleString("en-IN")}
                          </span>
                          <p className="mt-1 text-sm leading-relaxed text-white/70">{r.message}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <textarea
                    data-testid={`admin-reply-${en._id}`}
                    rows={2}
                    className={inputCls + " mt-3"}
                    placeholder="Write a reply — it lands in their email inbox"
                    value={replyDraft[en._id] || ""}
                    onChange={(e) => setReplyDraft({ ...replyDraft, [en._id]: e.target.value })}
                  />
                  <button
                    data-testid={`admin-reply-submit-${en._id}`}
                    onClick={() => sendReply(en)}
                    className="mt-2 bg-emerald-500 px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-white transition-colors hover:bg-emerald-400"
                  >
                    Send reply
                  </button>
                </div>
              ))}
          </div>
        )}
      </main>

      {tab !== "enquiries" && (
        <div className="fixed inset-x-0 bottom-6 z-30 flex justify-center px-6">
          <button
            data-testid={TID.admin.save}
            onClick={save}
            className="w-full max-w-md bg-crimson px-8 py-4 font-display text-sm font-bold text-white shadow-[0_20px_60px_-15px_rgba(220,47,68,0.5)] transition-colors hover:bg-crimsonlight"
          >
            Save changes — publishes everywhere instantly
          </button>
        </div>
      )}
    </div>
  );
}
